import { skipToken } from '@reduxjs/toolkit/query';
import TextOrNoData from 'components/content/TextOrNoData';
import { DataTable, type TableColumn } from 'components/table';
import type { VehicleFE } from 'domain/vehicle';
import { SystemAdminFormTypes } from 'helper/types';
import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetSystemFleetsAttributesQuery } from 'services/systemAdmin/fleets';
import { normalizeRecordData } from 'services/utils';
import { useListVehiclesV2Query } from 'services/vehiclesV2';
import { getSelectedFleet } from 'store/slices/settingsPageSlice';
import {
    getSelectedVehiclesFromFleetForm,
    getSystemFleetsFormType,
    setSelectedVehicles,
} from 'store/slices/systemFleet';

import EditFleetInfoTooltip from './EditFleetInfoTooltip';
import './_styles.scss';

const SelectVehiclesTable = () => {
    const dispatch = useDispatch();
    const selectedFleet = useSelector(getSelectedFleet);
    const selectedVehiclesFromEditedFleet = useSelector(getSelectedVehiclesFromFleetForm);
    const systemFleetsFormType = useSelector(getSystemFleetsFormType);
    const initializedFleetId = useRef<string>();

    const columns = useMemo<TableColumn<VehicleFE>[]>(
        () => [
            {
                key: 'vehicleName',
                title: 'Vehicle name',
                sortable: false,
                dataIndex: 'name',
            },
            {
                key: 'vin',
                title: 'VIN',
                sortable: false,
                dataIndex: 'vin',
            },
            {
                key: 'licensePlate',
                title: 'License plate',
                sortable: false,
                dataIndex: 'licensePlate',
                render: value => <TextOrNoData value={value} className='text-14' />,
            },
            {
                key: 'owner',
                title: 'Owner',
                sortable: false,
                dataIndex: 'owners',
                render: (_, vehicle) => (
                    <TextOrNoData value={vehicle?.owners.map(owner => owner.fullName).join(', ')} className='text-14' />
                ),
            },
        ],
        []
    );

    const { isLoading: isLoadingSystemVehicles, availableVehicles } = useListVehiclesV2Query(
        {},
        {
            refetchOnMountOrArgChange: true,
            selectFromResult: state => ({
                ...state,
                availableVehicles: normalizeRecordData(state.data).filter(
                    _ => _.fleetId === undefined || _.fleetId === selectedFleet?.id
                ),
            }),
        }
    );

    const {
        isLoading: isLoadingFleetVehicles,
        isFetching: isFetchingFleetVehicles,
        fleetVehicles,
        isSuccess: isFleetVehiclesSuccess,
    } = useGetSystemFleetsAttributesQuery(selectedFleet?.id ?? skipToken, {
        refetchOnMountOrArgChange: true,
        selectFromResult: state => {
            const vins = state.data?.vehicles.map(vehicle => vehicle.vin);
            return {
                ...state,
                fleetVehicles: vins?.length ? vins : [],
            };
        },
    });

    const handleSelectionChanged = (selectedRowKeys: React.Key[]) => {
        const vehicleVins = selectedRowKeys.filter((key): key is string => typeof key === 'string');

        dispatch(setSelectedVehicles(vehicleVins));
    };

    useEffect(() => {
        const fleetId = selectedFleet?.id;
        // This is needed in order to prevent race condition causing vehicle selction from the previous fleet to interfere with the selected vehicles of the current fleet, ensuring that the selected vehicles are correctly initialized when switching between fleets
        const needsInitialization =
            initializedFleetId.current !== fleetId || selectedVehiclesFromEditedFleet === undefined;

        if (
            systemFleetsFormType === SystemAdminFormTypes.EDIT_FORM &&
            fleetId &&
            needsInitialization &&
            isFleetVehiclesSuccess &&
            !isFetchingFleetVehicles
        ) {
            dispatch(setSelectedVehicles(fleetVehicles));
            initializedFleetId.current = fleetId;
        }
    }, [
        dispatch,
        fleetVehicles,
        isFetchingFleetVehicles,
        isFleetVehiclesSuccess,
        selectedFleet?.id,
        selectedVehiclesFromEditedFleet,
        systemFleetsFormType,
    ]);

    useEffect(() => {
        if (systemFleetsFormType !== SystemAdminFormTypes.EDIT_FORM) {
            initializedFleetId.current = undefined;
        }
    }, [systemFleetsFormType]);

    return (
        <DataTable
            stickyHeader
            data-testid='edit-fleets-select-vehicles-table'
            dataSource={availableVehicles}
            columns={columns}
            maxHeight='350px'
            showSelectionColumn={true}
            onSelectionChange={handleSelectionChanged}
            selectedRowKeys={selectedVehiclesFromEditedFleet}
            rowKey='vin'
            isLoading={isLoadingSystemVehicles || isLoadingFleetVehicles}
            empty={{
                title: 'No vehicles found',
                body: (
                    <div data-testid='edit-fleets-select-vehicles-table-no-content'>
                        All vehicles are already assigned to an existing fleet
                        <EditFleetInfoTooltip />
                    </div>
                ),
            }}
        />
    );
};

export default SelectVehiclesTable;
